import React, { useMemo, useState, useRef, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SupplierLayout } from '../../components/SupplierLayout';
import { useSupplierStore, Driver, Job } from '../../lib/supplierStore';
import { toast } from 'sonner';
import { Plus, Star, Phone, X, Truck as TruckIcon, User, IdCard, Calendar, Search, List, CalendarDays, MapPin, Clock, CheckCircle2, AlertCircle, History, Sparkles, Upload, Download, FileSpreadsheet } from 'lucide-react';
interface AddDriverModalProps {
  open: boolean;
  onClose: () => void;
}
function AddDriverModal({
  open,
  onClose
}: AddDriverModalProps) {
  const {
    trucks,
    drivers,
    addDriver
  } = useSupplierStore();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('+971 ');
  const [license, setLicense] = useState('');
  const [licenseExpiry, setLicenseExpiry] = useState('');
  const [truckId, setTruckId] = useState('');
  const availableTrucks = useMemo(() => {
    return trucks.filter(t => !t.assignedDriverId || !drivers.find(d => d.id === t.assignedDriverId));
  }, [trucks, drivers]);
  const reset = () => {
    setName('');
    setPhone('+971 ');
    setLicense('');
    setLicenseExpiry('');
    setTruckId('');
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim() || !license.trim() || !licenseExpiry) {
      toast.error('Please fill in all required fields.');
      return;
    }
    addDriver({
      name: name.trim(),
      phone: phone.trim(),
      license: license.trim(),
      licenseExpiry,
      truckId: truckId || undefined
    });
    toast.success(`${name.trim()} added to driver roster.`);
    reset();
    onClose();
  };
  return <AnimatePresence data-id="element-3327">
      {open && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" data-id="element-3328" />
          <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 10,
        scale: 0.98
      }} transition={{
        type: 'spring',
        stiffness: 350,
        damping: 30
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-3329">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden pointer-events-auto flex flex-col" data-id="element-3330">
              <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between shrink-0" data-id="element-3331">
                <div data-id="element-3332">
                  <h2 className="text-xl font-display font-bold text-gray-900" data-id="element-3333">
                    Add Driver
                  </h2>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-3334">
                    Register a new driver to your roster
                  </p>
                </div>
                <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 transition-colors" aria-label="Close" data-id="element-3335">
                  <X className="w-5 h-5 text-gray-500" data-id="element-3336" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-4" data-id="element-3337">
                <Field label="Full Name" icon={<User className="w-4 h-4 text-gray-400" data-id="element-3339" />} required data-id="element-3338">
                  <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Ahmed Khan" className="w-full pl-10 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" data-id="element-3340" />
                </Field>

                <Field label="Phone Number" icon={<Phone className="w-4 h-4 text-gray-400" data-id="element-3342" />} required data-id="element-3341">
                  <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+971 50 123 4567" className="w-full pl-10 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" data-id="element-3343" />
                </Field>

                <Field label="License Number" icon={<IdCard className="w-4 h-4 text-gray-400" data-id="element-3345" />} required data-id="element-3344">
                  <input type="text" value={license} onChange={e => setLicense(e.target.value)} placeholder="DL-123456" className="w-full pl-10 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" data-id="element-3346" />
                </Field>

                <Field label="License Expiry" icon={<Calendar className="w-4 h-4 text-gray-400" data-id="element-3348" />} required data-id="element-3347">
                  <input type="date" value={licenseExpiry} onChange={e => setLicenseExpiry(e.target.value)} className="w-full pl-10 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" data-id="element-3349" />
                </Field>

                <div data-id="element-3350">
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-2" data-id="element-3351">
                    Assign Truck (optional)
                  </label>
                  <div className="relative" data-id="element-3352">
                    <TruckIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-3353" />
                    <select value={truckId} onChange={e => setTruckId(e.target.value)} className="w-full pl-10 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 appearance-none" data-id="element-3354">
                      <option value="" data-id="element-3355">No truck assigned</option>
                      {availableTrucks.map(t => <option key={t.id} value={t.id} data-id="element-3356">
                          {t.plate} — {t.type}
                        </option>)}
                    </select>
                  </div>
                  {availableTrucks.length === 0 && <p className="text-[11px] text-amber-700 mt-1.5" data-id="element-3357">
                      All trucks are currently assigned. You can re-assign from
                      the Fleet page.
                    </p>}
                </div>
              </form>

              <div className="px-6 py-4 border-t border-gray-100 flex gap-3 justify-end shrink-0 bg-gray-50/50" data-id="element-3358">
                <button type="button" onClick={onClose} className="px-5 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl text-sm font-bold transition-colors" data-id="element-3359">
                  Cancel
                </button>
                <button type="button" onClick={handleSubmit} className="px-5 py-2.5 bg-gray-900 hover:bg-black text-white rounded-xl text-sm font-bold transition-colors flex items-center gap-2" data-id="element-3360">
                  <Plus className="w-4 h-4" data-id="element-3361" /> Add Driver
                </button>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}
function Field({
  label,
  icon,
  required,
  children
}: {
  label: string;
  icon: React.ReactNode;
  required?: boolean;
  children: React.ReactNode;
}) {
  return <div data-id="element-3362">
      <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-2" data-id="element-3363">
        {label} {required && <span className="text-brand-500" data-id="element-3364">*</span>}
      </label>
      <div className="relative" data-id="element-3365">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-3366">
          {icon}
        </div>
        {children}
      </div>
    </div>;
}
function formatTime(hour: number) {
  const h = Math.floor(hour);
  const m = (hour - h) * 60;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${m === 0 ? '00' : m} ${ampm}`;
}
function getJobStatusColor(status: string): string {
  switch (status) {
    case 'completed':
      return 'bg-green-50 text-green-700 border-green-100';
    case 'in_progress':
    case 'at_pickup':
    case 'picked':
    case 'at_dropoff':
      return 'bg-purple-50 text-purple-700 border-purple-100';
    case 'accepted':
      return 'bg-blue-50 text-blue-700 border-blue-100';
    case 'pending_acceptance':
      return 'bg-yellow-50 text-yellow-700 border-yellow-100';
    default:
      return 'bg-gray-50 text-gray-700 border-gray-100';
  }
}
function getJobStatusLabel(status: string): string {
  const map: Record<string, string> = {
    pending_acceptance: 'Pending',
    accepted: 'Accepted',
    in_progress: 'In progress',
    at_pickup: 'At pickup',
    picked: 'Picked',
    at_dropoff: 'At dropoff',
    completed: 'Completed',
    unassigned: 'Unassigned'
  };
  return map[status] || status;
}
function DriverDetailDrawer({
  driver,
  jobs,
  trucks,
  drivers,
  onClose
}: {
  driver: Driver | null;
  jobs: Job[];
  trucks: ReturnType<typeof useSupplierStore>['trucks'];
  drivers: Driver[];
  onClose: () => void;
}) {
  const {
    assignTruckToDriver
  } = useSupplierStore();
  const [truckPickerOpen, setTruckPickerOpen] = useState(false);
  if (!driver) return null;
  const driverJobs = jobs.filter(j => j.assignedDriverId === driver.id);
  const upcoming = driverJobs.filter(j => j.status !== 'completed').sort((a, b) => {
    const order = {
      today: 0,
      tomorrow: 1,
      yesterday: -1
    } as Record<string, number>;
    const oA = order[a.scheduledDate] ?? 2;
    const oB = order[b.scheduledDate] ?? 2;
    if (oA !== oB) return oA - oB;
    return a.startHour - b.startHour;
  });
  const completed = driverJobs.filter(j => j.status === 'completed').sort((a, b) => (b.stageHistory?.completed || 0) - (a.stageHistory?.completed || 0));
  const truck = trucks.find(t => t.id === driver.assignedTruckId);
  const availableTrucks = trucks.filter(t => {
    if (t.id === driver.assignedTruckId) return false;
    const heldBy = drivers.find(d => d.assignedTruckId === t.id);
    return !heldBy;
  });
  const handleAssignTruck = (truckId: string) => {
    assignTruckToDriver(driver.id, truckId);
    const t = trucks.find(x => x.id === truckId);
    toast.success(`${t?.plate || 'Truck'} assigned to ${driver.name}.`);
    setTruckPickerOpen(false);
  };
  const handleUnassignTruck = () => {
    assignTruckToDriver(driver.id, null);
    toast.success(`Truck unassigned from ${driver.name}.`);
  };
  return <AnimatePresence data-id="element-3367">
      {driver && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/40 z-50" data-id="element-3368" />
          <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 30
      }} className="fixed top-0 right-0 bottom-0 w-full max-w-xl bg-white shadow-2xl z-50 flex flex-col" data-id="element-3369">
            <div className="px-6 py-5 border-b border-gray-100 flex items-start justify-between shrink-0" data-id="element-3370">
              <div className="flex items-center gap-4" data-id="element-3371">
                <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-bold text-xl" data-id="element-3372">
                  {driver.name.charAt(0)}
                </div>
                <div data-id="element-3373">
                  <h2 className="text-xl font-display font-bold text-gray-900" data-id="element-3374">
                    {driver.name}
                  </h2>
                  <p className="text-xs text-gray-500 flex items-center gap-1.5 mt-0.5" data-id="element-3375">
                    <Phone className="w-3 h-3" data-id="element-3376" /> {driver.phone}
                  </p>
                  <div className="flex items-center gap-3 mt-1.5" data-id="element-3377">
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-gray-700" data-id="element-3378">
                      <Star className="w-3.5 h-3.5 text-amber-400 fill-current" data-id="element-3379" />
                      {driver.rating}
                    </span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${driver.status === 'On Duty' ? 'bg-green-100 text-green-700' : driver.status === 'Off Duty' ? 'bg-gray-100 text-gray-600' : 'bg-orange-100 text-orange-700'}`} data-id="element-3380">
                      {driver.status}
                    </span>
                  </div>
                </div>
              </div>
              <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 transition-colors shrink-0" aria-label="Close" data-id="element-3381">
                <X className="w-5 h-5 text-gray-500" data-id="element-3382" />
              </button>
            </div>

            <div className="px-6 py-4 border-b border-gray-100 grid grid-cols-2 gap-3 bg-gray-50/50 shrink-0" data-id="element-3383">
              <Stat label="Upcoming" value={upcoming.length} data-id="element-3384" />
              <Stat label="Completed" value={completed.length} accent="green" data-id="element-3385" />
            </div>

            <div className="flex-1 overflow-y-auto" data-id="element-3386">
              {/* Truck assignment panel */}
              <div className="px-6 pt-5 pb-3" data-id="element-3387">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-3388">
                  <TruckIcon className="w-3.5 h-3.5" data-id="element-3389" />
                  Assigned truck
                </h3>
                {truck ? <div className="p-4 rounded-2xl border border-gray-200 bg-white" data-id="element-3390">
                    <div className="flex items-center justify-between gap-3" data-id="element-3391">
                      <div className="flex items-center gap-3 min-w-0" data-id="element-3392">
                        <div className="w-11 h-11 rounded-xl bg-gray-900 flex items-center justify-center shrink-0" data-id="element-3393">
                          <TruckIcon className="w-5 h-5 text-white" data-id="element-3394" />
                        </div>
                        <div className="min-w-0" data-id="element-3395">
                          <p className="font-bold text-gray-900 truncate" data-id="element-3396">
                            {truck.plate}
                          </p>
                          <p className="text-xs text-gray-500 truncate" data-id="element-3397">
                            {truck.type} · {truck.capacity}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0" data-id="element-3398">
                        <button onClick={() => setTruckPickerOpen(v => !v)} className="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold transition-colors" data-id="element-3399">
                          Change
                        </button>
                        <button onClick={handleUnassignTruck} className="px-3 py-1.5 rounded-lg bg-white border border-gray-200 hover:bg-red-50 hover:border-red-200 hover:text-red-700 text-gray-700 text-xs font-bold transition-colors" data-id="element-3400">
                          Unassign
                        </button>
                      </div>
                    </div>
                    {truckPickerOpen && <TruckPickerList trucks={availableTrucks} onPick={handleAssignTruck} onCancel={() => setTruckPickerOpen(false)} data-id="element-3401" />}
                  </div> : <div className="p-4 rounded-2xl border border-amber-200 bg-amber-50/40" data-id="element-3402">
                    <div className="flex items-center gap-3 mb-3" data-id="element-3403">
                      <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0" data-id="element-3404">
                        <AlertCircle className="w-5 h-5 text-amber-700" data-id="element-3405" />
                      </div>
                      <div className="min-w-0" data-id="element-3406">
                        <p className="font-bold text-gray-900 text-sm" data-id="element-3407">
                          No truck assigned
                        </p>
                        <p className="text-xs text-gray-600" data-id="element-3408">
                          Assign a truck so this driver can take jobs.
                        </p>
                      </div>
                    </div>
                    {!truckPickerOpen ? <button onClick={() => setTruckPickerOpen(true)} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gray-900 hover:bg-black text-white text-sm font-bold transition-colors" data-id="element-3409">
                        <TruckIcon className="w-4 h-4" data-id="element-3410" /> Assign a truck
                      </button> : <TruckPickerList trucks={availableTrucks} onPick={handleAssignTruck} onCancel={() => setTruckPickerOpen(false)} data-id="element-3411" />}
                  </div>}
              </div>

              {/* Upcoming */}
              <div className="px-6 pt-5 pb-3" data-id="element-3412">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-3413">
                  <CalendarDays className="w-3.5 h-3.5" data-id="element-3414" />
                  Upcoming schedule ({upcoming.length})
                </h3>
                {upcoming.length === 0 ? <p className="text-sm text-gray-500 py-4 text-center bg-gray-50 rounded-xl" data-id="element-3415">
                    No upcoming jobs.
                  </p> : <div className="space-y-2" data-id="element-3416">
                    {upcoming.map(j => <JobRow key={j.id} job={j} data-id="element-3417" />)}
                  </div>}
              </div>

              {/* Completed */}
              <div className="px-6 pt-3 pb-6 border-t border-gray-100 mt-2" data-id="element-3418">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-3419">
                  <CheckCircle2 className="w-3.5 h-3.5" data-id="element-3420" />
                  Completed jobs ({completed.length})
                </h3>
                {completed.length === 0 ? <p className="text-sm text-gray-500 py-4 text-center bg-gray-50 rounded-xl" data-id="element-3421">
                    No completed jobs yet.
                  </p> : <div className="space-y-2" data-id="element-3422">
                    {completed.map(j => <JobRow key={j.id} job={j} data-id="element-3423" />)}
                  </div>}
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}
function TruckPickerList({
  trucks,
  onPick,
  onCancel
}: {
  trucks: ReturnType<typeof useSupplierStore>['trucks'];
  onPick: (truckId: string) => void;
  onCancel: () => void;
}) {
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const matching = useMemo(() => {
    if (!q) return trucks;
    return trucks.filter(t => t.plate.toLowerCase().includes(q) || t.type.toLowerCase().includes(q) || t.capacity.toLowerCase().includes(q) || t.status.toLowerCase().includes(q));
  }, [trucks, q]);
  // Suggested: trucks that are ready to take a driver immediately.
  // Priority: Idle first (no current work), then Active (operating but driverless).
  const suggested = useMemo(() => matching.filter(t => t.status === 'Idle' || t.status === 'Active').sort((a, b) => {
    const order = {
      Idle: 0,
      Active: 1
    } as Record<string, number>;
    return (order[a.status] ?? 2) - (order[b.status] ?? 2);
  }).slice(0, 4), [matching]);
  const suggestedIds = new Set(suggested.map(t => t.id));
  const others = matching.filter(t => !suggestedIds.has(t.id));
  return <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-3424">
      <div className="flex items-center justify-between mb-2.5" data-id="element-3425">
        <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-3426">
          Available trucks ({trucks.length})
        </p>
        <button onClick={onCancel} className="text-[11px] font-bold text-gray-500 hover:text-gray-900" data-id="element-3427">
          Cancel
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-3" data-id="element-3428">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-3429" />
        <input type="text" autoFocus value={query} onChange={e => setQuery(e.target.value)} placeholder="Search by plate, type, capacity..." className="w-full pl-9 pr-8 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" data-id="element-3430" />
        {query && <button onClick={() => setQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-200" aria-label="Clear search" data-id="element-3431">
            <X className="w-3 h-3 text-gray-500" data-id="element-3432" />
          </button>}
      </div>

      {trucks.length === 0 ? <p className="text-xs text-gray-500 py-3 text-center bg-gray-50 rounded-lg" data-id="element-3433">
          No available trucks — all are currently assigned.
        </p> : matching.length === 0 ? <p className="text-xs text-gray-500 py-3 text-center bg-gray-50 rounded-lg" data-id="element-3434">
          No trucks match "{query}".
        </p> : <div className="max-h-72 overflow-y-auto space-y-3 pr-1" data-id="element-3435">
          {/* Suggested */}
          {!q && suggested.length > 0 && <div data-id="element-3436">
              <p className="text-[10px] font-bold text-brand-600 uppercase tracking-wider mb-1.5 flex items-center gap-1" data-id="element-3437">
                <Sparkles className="w-3 h-3" data-id="element-3438" /> Suggested · ready now
              </p>
              <div className="space-y-1.5" data-id="element-3439">
                {suggested.map(t => <TruckOptionRow key={t.id} truck={t} onPick={onPick} highlight data-id="element-3440" />)}
              </div>
            </div>}
          {/* Others / search results */}
          {(!q && others.length > 0 || q) && <div data-id="element-3441">
              {!q && suggested.length > 0 && <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5 mt-2" data-id="element-3442">
                  All other trucks ({others.length})
                </p>}
              <div className="space-y-1.5" data-id="element-3443">
                {(q ? matching : others).map(t => <TruckOptionRow key={t.id} truck={t} onPick={onPick} data-id="element-3444" />)}
              </div>
            </div>}
        </div>}
    </div>;
}
function TruckOptionRow({
  truck: t,
  onPick,
  highlight
}: {
  truck: ReturnType<typeof useSupplierStore>['trucks'][number];
  onPick: (truckId: string) => void;
  highlight?: boolean;
}) {
  const statusColor = t.status === 'Idle' ? 'bg-green-100 text-green-700' : t.status === 'Active' ? 'bg-blue-100 text-blue-700' : t.status === 'Maintenance' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700';
  return <button onClick={() => onPick(t.id)} className={`w-full flex items-center justify-between gap-3 p-2.5 rounded-lg border transition-colors text-left ${highlight ? 'bg-brand-50/40 border-brand-200 hover:border-brand-400 hover:bg-brand-50' : 'bg-white border-gray-200 hover:border-brand-300 hover:bg-brand-50/30'}`} data-id="element-3445">
      <div className="flex items-center gap-2.5 min-w-0" data-id="element-3446">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${highlight ? 'bg-brand-500/10' : 'bg-gray-100'}`} data-id="element-3447">
          <TruckIcon className={`w-4 h-4 ${highlight ? 'text-brand-600' : 'text-gray-600'}`} data-id="element-3448" />
        </div>
        <div className="min-w-0" data-id="element-3449">
          <div className="flex items-center gap-1.5" data-id="element-3450">
            <p className="font-bold text-gray-900 text-sm truncate" data-id="element-3451">
              {t.plate}
            </p>
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${statusColor}`} data-id="element-3452">
              {t.status}
            </span>
          </div>
          <p className="text-[11px] text-gray-500 truncate" data-id="element-3453">
            {t.type} · {t.capacity}
          </p>
        </div>
      </div>
      <span className="text-[10px] font-bold text-brand-600 shrink-0" data-id="element-3454">
        Assign →
      </span>
    </button>;
}
function Stat({
  label,
  value,
  accent,
  small
}: {
  label: string;
  value: number | string;
  accent?: 'green';
  small?: boolean;
}) {
  return <div className="bg-white border border-gray-200 rounded-xl p-3" data-id="element-3455">
      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-3456">
        {label}
      </p>
      <p className={`font-display font-bold text-gray-900 ${small ? 'text-base' : 'text-xl'} ${accent === 'green' ? 'text-green-600' : ''}`} data-id="element-3457">
        {value}
      </p>
    </div>;
}
function JobHistoryView({
  jobs,
  drivers,
  search,
  onSelectDriver
}: {
  jobs: Job[];
  drivers: Driver[];
  search: string;
  onSelectDriver: (id: string) => void;
}) {
  const [statusFilter, setStatusFilter] = useState<'all' | 'completed' | 'active'>('all');
  // Sort newest first using stageHistory.completed or pending_acceptance
  const sorted = useMemo(() => {
    return [...jobs].filter(j => j.assignedDriverId) // only jobs that had a driver
    .sort((a, b) => {
      const aT = a.stageHistory?.completed || a.stageHistory?.accepted || a.stageHistory?.pending_acceptance || 0;
      const bT = b.stageHistory?.completed || b.stageHistory?.accepted || b.stageHistory?.pending_acceptance || 0;
      return bT - aT;
    });
  }, [jobs]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sorted.filter(j => {
      if (statusFilter === 'completed' && j.status !== 'completed') return false;
      if (statusFilter === 'active' && j.status === 'completed') return false;
      if (!q) return true;
      const driver = drivers.find(d => d.id === j.assignedDriverId);
      return j.id.toLowerCase().includes(q) || j.serviceType.toLowerCase().includes(q) || (j.customerName || '').toLowerCase().includes(q) || j.pickup.toLowerCase().includes(q) || j.dropoff.toLowerCase().includes(q) || (driver?.name || '').toLowerCase().includes(q);
    });
  }, [sorted, statusFilter, search, drivers]);
  const formatRelative = (ts?: number) => {
    if (!ts) return '—';
    const diffMin = Math.round((Date.now() - ts) / 60000);
    if (diffMin < 60) return `${diffMin}m ago`;
    const h = Math.round(diffMin / 60);
    if (h < 48) return `${h}h ago`;
    const d = Math.round(h / 24);
    return `${d}d ago`;
  };
  return <div className="space-y-4" data-id="element-3458">
      {/* Status filter chips */}
      <div className="flex items-center gap-2" data-id="element-3459">
        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-3460">
          Filter
        </span>
        {[{
        id: 'all',
        label: 'All jobs',
        count: sorted.length
      }, {
        id: 'completed',
        label: 'Completed',
        count: sorted.filter(j => j.status === 'completed').length
      }, {
        id: 'active',
        label: 'Active',
        count: sorted.filter(j => j.status !== 'completed').length
      }].map(f => <button key={f.id} onClick={() => setStatusFilter(f.id as typeof statusFilter)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${statusFilter === f.id ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-100'}`} data-id="element-3461">
            {f.label}
            <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${statusFilter === f.id ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'}`} data-id="element-3462">
              {f.count}
            </span>
          </button>)}
        <span className="text-xs text-gray-500 ml-auto" data-id="element-3463">
          Showing {filtered.length} {filtered.length === 1 ? 'job' : 'jobs'}
        </span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-3464">
        <div className="overflow-x-auto" data-id="element-3465">
          <table className="w-full text-left text-sm" data-id="element-3466">
            <thead className="bg-gray-50 text-gray-500 font-bold uppercase tracking-wider text-[10px]" data-id="element-3467">
              <tr data-id="element-3468">
                <th className="px-5 py-3" data-id="element-3469">Job</th>
                <th className="px-5 py-3" data-id="element-3470">Customer</th>
                <th className="px-5 py-3" data-id="element-3471">Driver</th>
                <th className="px-5 py-3" data-id="element-3472">Route</th>
                <th className="px-5 py-3" data-id="element-3473">When</th>
                <th className="px-5 py-3" data-id="element-3474">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-3475">
              {filtered.map(j => {
              const driver = drivers.find(d => d.id === j.assignedDriverId);
              const completedAt = j.stageHistory?.completed;
              return <tr key={j.id} className="hover:bg-gray-50/50 transition-colors" data-id="element-3476">
                    <td className="px-5 py-4 align-top" data-id="element-3477">
                      <p className="font-bold text-gray-900 text-sm" data-id="element-3478">{j.id}</p>
                      <p className="text-xs text-gray-500" data-id="element-3479">{j.serviceType}</p>
                    </td>
                    <td className="px-5 py-4 align-top" data-id="element-3480">
                      <p className="text-sm text-gray-900 truncate max-w-[180px]" data-id="element-3481">
                        {j.customerName || '—'}
                      </p>
                      {j.customerPhone && <p className="text-[11px] text-gray-500" data-id="element-3482">
                          {j.customerPhone}
                        </p>}
                    </td>
                    <td className="px-5 py-4 align-top" data-id="element-3483">
                      {driver ? <button onClick={() => onSelectDriver(driver.id)} className="flex items-center gap-2 hover:underline group" data-id="element-3484">
                          <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-bold text-xs shrink-0" data-id="element-3485">
                            {driver.name.charAt(0)}
                          </div>
                          <span className="font-bold text-gray-900 text-sm group-hover:text-brand-600 transition-colors" data-id="element-3486">
                            {driver.name}
                          </span>
                        </button> : <span className="text-xs text-gray-400" data-id="element-3487">
                          Unassigned
                        </span>}
                    </td>
                    <td className="px-5 py-4 align-top" data-id="element-3488">
                      <p className="text-xs text-gray-700 truncate max-w-[220px]" data-id="element-3489">
                        {j.pickup}
                      </p>
                      {j.dropoff && j.dropoff !== j.pickup && <p className="text-[11px] text-gray-500 truncate max-w-[220px]" data-id="element-3490">
                          → {j.dropoff}
                        </p>}
                    </td>
                    <td className="px-5 py-4 align-top whitespace-nowrap" data-id="element-3491">
                      <p className="text-xs text-gray-700 capitalize" data-id="element-3492">
                        {j.scheduledDate}
                      </p>
                      <p className="text-[11px] text-gray-500" data-id="element-3493">
                        {completedAt ? formatRelative(completedAt) : `${j.startHour}:00`}
                      </p>
                    </td>
                    <td className="px-5 py-4 align-top" data-id="element-3494">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border ${j.status === 'completed' ? 'bg-green-50 text-green-700 border-green-100' : j.status === 'unassigned' ? 'bg-gray-50 text-gray-600 border-gray-100' : 'bg-purple-50 text-purple-700 border-purple-100'}`} data-id="element-3495">
                        {j.status === 'completed' ? <CheckCircle2 className="w-3 h-3 mr-1" data-id="element-3496" /> : null}
                        {j.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                  </tr>;
            })}
              {filtered.length === 0 && <tr data-id="element-3497">
                  <td colSpan={6} className="px-5 py-12 text-center text-gray-500 text-sm" data-id="element-3498">
                    No jobs match your search.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}
function JobRow({
  job
}: {
  job: Job;
}) {
  return <div className="p-3 rounded-xl border border-gray-200 bg-white" data-id="element-3499">
      <div className="flex items-center justify-between mb-1.5 gap-2" data-id="element-3500">
        <div className="flex items-center gap-2 min-w-0" data-id="element-3501">
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider shrink-0" data-id="element-3502">
            {job.id}
          </span>
          <span className="font-bold text-gray-900 text-sm truncate" data-id="element-3503">
            {job.serviceType}
          </span>
        </div>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider border shrink-0 ${getJobStatusColor(job.status)}`} data-id="element-3504">
          {getJobStatusLabel(job.status)}
        </span>
      </div>
      <div className="flex items-center gap-3 text-xs text-gray-600 mb-1" data-id="element-3505">
        <span className="flex items-center gap-1" data-id="element-3506">
          <Calendar className="w-3 h-3 text-gray-400" data-id="element-3507" />
          {job.scheduledDate === 'today' ? 'Today' : job.scheduledDate === 'tomorrow' ? 'Tomorrow' : job.scheduledDate === 'yesterday' ? 'Yesterday' : job.scheduledDate}
        </span>
        <span className="flex items-center gap-1" data-id="element-3508">
          <Clock className="w-3 h-3 text-gray-400" data-id="element-3509" />
          {formatTime(job.startHour)} ({job.durationHours}h)
        </span>
      </div>
      <p className="text-xs text-gray-500 truncate" data-id="element-3510">
        <MapPin className="w-3 h-3 inline mr-1 text-gray-400" data-id="element-3511" />
        {job.pickup}
        {job.dropoff && job.dropoff !== job.pickup ? ` → ${job.dropoff}` : ''}
      </p>
    </div>;
}
export function SupplierDrivers() {
  const {
    drivers,
    trucks,
    jobs,
    addDriver
  } = useSupplierStore();
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [view, setView] = useState<'list' | 'schedule' | 'history'>('list');
  const [openDriverId, setOpenDriverId] = useState<string | null>(null);
  const filtered = useMemo(() => {
    return drivers.filter(d => {
      if (search && !d.name.toLowerCase().includes(search.toLowerCase()) && !d.phone.includes(search) && !d.license.toLowerCase().includes(search.toLowerCase())) return false;
      if (statusFilter !== 'all' && d.status !== statusFilter) return false;
      return true;
    });
  }, [drivers, search, statusFilter]);
  const unassignedCount = drivers.filter(d => !d.assignedTruckId).length;
  const openDriver = drivers.find(d => d.id === openDriverId) || null;
  return <SupplierLayout data-id="element-3512">
      <div className="max-w-6xl mx-auto" data-id="element-3513">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4" data-id="element-3514">
          <div data-id="element-3515">
            <h1 className="text-2xl font-display font-bold text-gray-900" data-id="element-3516">
              Driver Roster
            </h1>
            <p className="text-sm text-gray-500 mt-1" data-id="element-3517">
              {drivers.length} registered driver
              {drivers.length === 1 ? '' : 's'}
              {unassignedCount > 0 && <>
                  {' '}
                  ·{' '}
                  <span className="text-amber-700 font-semibold" data-id="element-3518">
                    {unassignedCount} without a truck
                  </span>
                </>}
            </p>
          </div>
          <div className="flex items-center gap-2" data-id="element-3519">
            <button onClick={() => setBulkUploadOpen(true)} className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 rounded-xl text-sm font-bold transition-colors shadow-sm" data-id="element-3520">
              <Upload className="w-4 h-4" data-id="element-3521" /> Bulk Upload
            </button>
            <button onClick={() => setModalOpen(true)} className="flex items-center justify-center gap-2 px-5 py-2.5 bg-gray-900 hover:bg-black text-white rounded-xl text-sm font-bold transition-colors shadow-sm" data-id="element-3522">
              <Plus className="w-4 h-4" data-id="element-3523" /> Add Driver
            </button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-4" data-id="element-3524">
          <div className="relative flex-1" data-id="element-3525">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-3526" />
            <input type="text" placeholder={view === 'history' ? 'Search jobs by id, service, customer, route, or driver...' : 'Search by name, phone, or license...'} value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" data-id="element-3527" />
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" data-id="element-3528">
            <option value="all" data-id="element-3529">All Status</option>
            <option value="On Duty" data-id="element-3530">On Duty</option>
            <option value="Off Duty" data-id="element-3531">Off Duty</option>
            <option value="On Leave" data-id="element-3532">On Leave</option>
          </select>
          {/* View toggle */}
          <div className="flex items-center bg-gray-100 rounded-xl p-0.5 shrink-0" data-id="element-3533">
            <button onClick={() => setView('list')} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-all ${view === 'list' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-3534">
              <List className="w-3.5 h-3.5" data-id="element-3535" /> List
            </button>
            <button onClick={() => setView('schedule')} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-all ${view === 'schedule' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-3536">
              <CalendarDays className="w-3.5 h-3.5" data-id="element-3537" /> Schedule
            </button>
            <button onClick={() => setView('history')} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-all ${view === 'history' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-3538">
              <History className="w-3.5 h-3.5" data-id="element-3539" /> History
            </button>
          </div>
        </div>

        {view === 'history' ? <JobHistoryView jobs={jobs} drivers={drivers} search={search} onSelectDriver={setOpenDriverId} data-id="element-3540" /> : view === 'list' ? <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-3541">
            <div className="overflow-x-auto" data-id="element-3542">
              <table className="w-full text-left text-sm" data-id="element-3543">
                <thead className="bg-gray-50 text-gray-500 font-bold uppercase tracking-wider text-[10px]" data-id="element-3544">
                  <tr data-id="element-3545">
                    <th className="px-6 py-4" data-id="element-3546">Driver</th>
                    <th className="px-6 py-4" data-id="element-3547">Contact</th>
                    <th className="px-6 py-4" data-id="element-3548">License</th>
                    <th className="px-6 py-4" data-id="element-3549">Assigned Truck</th>
                    <th className="px-6 py-4" data-id="element-3550">Rating</th>
                    <th className="px-6 py-4" data-id="element-3551">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-3552">
                  {filtered.map(driver => {
                const truck = trucks.find(t => t.id === driver.assignedTruckId);
                const isExpiring = new Date(driver.licenseExpiry).getTime() - Date.now() < 30 * 24 * 60 * 60 * 1000;
                return <tr key={driver.id} onClick={() => setOpenDriverId(driver.id)} className="hover:bg-gray-50/50 transition-colors cursor-pointer" data-id="element-3553">
                        <td className="px-6 py-4" data-id="element-3554">
                          <div className="flex items-center gap-3" data-id="element-3555">
                            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-bold" data-id="element-3556">
                              {driver.name.charAt(0)}
                            </div>
                            <span className="font-bold text-gray-900 whitespace-nowrap" data-id="element-3557">
                              {driver.name}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-gray-600 whitespace-nowrap" data-id="element-3558">
                          <div className="flex items-center gap-1.5" data-id="element-3559">
                            <Phone className="w-3.5 h-3.5" data-id="element-3560" /> {driver.phone}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-3561">
                          <p className="font-medium text-gray-900" data-id="element-3562">
                            {driver.license}
                          </p>
                          <p className={`text-[10px] mt-0.5 font-bold ${isExpiring ? 'text-red-600' : 'text-gray-400'}`} data-id="element-3563">
                            Exp: {driver.licenseExpiry}
                          </p>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-3564">
                          {truck ? <div data-id="element-3565">
                              <p className="font-bold text-gray-900" data-id="element-3566">
                                {truck.plate}
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-3567">
                                {truck.type}
                              </p>
                            </div> : <button onClick={e => {
                      e.stopPropagation();
                      setOpenDriverId(driver.id);
                    }} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-amber-50 hover:bg-amber-100 text-amber-700 text-[10px] font-bold uppercase tracking-wider transition-colors" data-id="element-3568">
                              <TruckIcon className="w-3 h-3" data-id="element-3569" /> Assign truck
                            </button>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-3570">
                          <div className="flex items-center gap-1 font-bold text-gray-900" data-id="element-3571">
                            <Star className="w-4 h-4 text-amber-400 fill-current" data-id="element-3572" />{' '}
                            {driver.rating}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-3573">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider ${driver.status === 'On Duty' ? 'bg-green-100 text-green-700' : driver.status === 'Off Duty' ? 'bg-gray-100 text-gray-600' : 'bg-orange-100 text-orange-700'}`} data-id="element-3574">
                            {driver.status}
                          </span>
                        </td>
                      </tr>;
              })}
                  {filtered.length === 0 && <tr data-id="element-3575">
                      <td colSpan={6} className="px-6 py-12 text-center text-gray-500 text-sm" data-id="element-3576">
                        No drivers match your filters.
                      </td>
                    </tr>}
                </tbody>
              </table>
            </div>
          </div> :
      // Schedule view
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3577">
            {filtered.map(driver => {
          const truck = trucks.find(t => t.id === driver.assignedTruckId);
          const dJobs = jobs.filter(j => j.assignedDriverId === driver.id);
          const upcoming = dJobs.filter(j => j.status !== 'completed');
          const completedCount = dJobs.filter(j => j.status === 'completed').length;
          const todayCount = upcoming.filter(j => j.scheduledDate === 'today').length;
          const tomorrowCount = upcoming.filter(j => j.scheduledDate === 'tomorrow').length;
          const nextJob = upcoming.sort((a, b) => {
            const order = {
              today: 0,
              tomorrow: 1
            } as Record<string, number>;
            const oA = order[a.scheduledDate] ?? 2;
            const oB = order[b.scheduledDate] ?? 2;
            if (oA !== oB) return oA - oB;
            return a.startHour - b.startHour;
          })[0];
          return <button key={driver.id} onClick={() => setOpenDriverId(driver.id)} className="text-left bg-white rounded-2xl border border-gray-200 hover:border-brand-300 hover:shadow-md transition-all p-5" data-id="element-3578">
                  <div className="flex items-center justify-between mb-4 gap-3" data-id="element-3579">
                    <div className="flex items-center gap-3 min-w-0" data-id="element-3580">
                      <div className="w-11 h-11 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-bold shrink-0" data-id="element-3581">
                        {driver.name.charAt(0)}
                      </div>
                      <div className="min-w-0" data-id="element-3582">
                        <h3 className="font-bold text-gray-900 truncate" data-id="element-3583">
                          {driver.name}
                        </h3>
                        <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5" data-id="element-3584">
                          {truck ? <>
                              <TruckIcon className="w-3 h-3" data-id="element-3585" /> {truck.plate}
                            </> : <span className="inline-flex items-center gap-1 text-amber-700 font-semibold" data-id="element-3586">
                              <AlertCircle className="w-3 h-3" data-id="element-3587" /> No truck
                            </span>}
                        </p>
                      </div>
                    </div>
                    <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${driver.status === 'On Duty' ? 'bg-green-100 text-green-700' : driver.status === 'Off Duty' ? 'bg-gray-100 text-gray-600' : 'bg-orange-100 text-orange-700'}`} data-id="element-3588">
                      {driver.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-3 gap-2 mb-4" data-id="element-3589">
                    <div className="bg-gray-50 rounded-lg p-2 text-center" data-id="element-3590">
                      <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-3591">
                        Today
                      </p>
                      <p className="font-display font-bold text-gray-900 text-lg" data-id="element-3592">
                        {todayCount}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2 text-center" data-id="element-3593">
                      <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-3594">
                        Tomorrow
                      </p>
                      <p className="font-display font-bold text-gray-900 text-lg" data-id="element-3595">
                        {tomorrowCount}
                      </p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-2 text-center" data-id="element-3596">
                      <p className="text-[9px] font-bold text-green-700 uppercase tracking-wider" data-id="element-3597">
                        Completed
                      </p>
                      <p className="font-display font-bold text-green-700 text-lg" data-id="element-3598">
                        {completedCount}
                      </p>
                    </div>
                  </div>

                  {nextJob ? <div className="p-3 rounded-xl bg-gray-50 border border-gray-100" data-id="element-3599">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-3600">
                        Next up
                      </p>
                      <p className="font-bold text-gray-900 text-sm truncate" data-id="element-3601">
                        {nextJob.serviceType}
                      </p>
                      <p className="text-xs text-gray-500 flex items-center gap-2 mt-1" data-id="element-3602">
                        <span className="flex items-center gap-1" data-id="element-3603">
                          <Calendar className="w-3 h-3" data-id="element-3604" />
                          {nextJob.scheduledDate === 'today' ? 'Today' : 'Tomorrow'}
                        </span>
                        <span className="flex items-center gap-1" data-id="element-3605">
                          <Clock className="w-3 h-3" data-id="element-3606" />
                          {formatTime(nextJob.startHour)}
                        </span>
                      </p>
                    </div> : <div className="p-3 rounded-xl bg-gray-50 border border-dashed border-gray-200 text-center" data-id="element-3607">
                      <p className="text-xs text-gray-500" data-id="element-3608">No upcoming jobs</p>
                    </div>}
                </button>;
        })}
            {filtered.length === 0 && <div className="md:col-span-2 bg-white rounded-2xl border border-gray-200 p-12 text-center text-gray-500 text-sm" data-id="element-3609">
                No drivers match your filters.
              </div>}
          </div>}
      </div>

      <AddDriverModal open={modalOpen} onClose={() => setModalOpen(false)} data-id="element-3610" />
      <AnimatePresence data-id="element-3611">
        {bulkUploadOpen && <BulkUploadDriversModal onClose={() => setBulkUploadOpen(false)} onImport={rows => {
        let count = 0;
        rows.forEach(r => {
          addDriver({
            name: r.name,
            phone: r.phone,
            license: r.license,
            licenseExpiry: r.licenseExpiry,
            status: r.status,
            rating: r.rating
          });
          count++;
        });
        toast.success(`${count} driver${count !== 1 ? 's' : ''} imported successfully`);
        setBulkUploadOpen(false);
      }} data-id="element-3612" />}
      </AnimatePresence>
      <DriverDetailDrawer driver={openDriver} jobs={jobs} trucks={trucks} drivers={drivers} onClose={() => setOpenDriverId(null)} data-id="element-3613" />
    </SupplierLayout>;
}
// ============ Bulk Upload Drivers Modal ============
type ParsedDriverRow = {
  rowNumber: number;
  name: string;
  phone: string;
  license: string;
  licenseExpiry: string;
  status: Driver['status'];
  rating: number;
  errors: string[];
};
function parseDriverCSV(text: string): string[][] {
  const rows: string[][] = [];
  const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0);
  for (const line of lines) {
    const fields: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (c === ',' && !inQuotes) {
        fields.push(current.trim());
        current = '';
      } else {
        current += c;
      }
    }
    fields.push(current.trim());
    rows.push(fields);
  }
  return rows;
}
function BulkUploadDriversModal({
  onClose,
  onImport
}: {
  onClose: () => void;
  onImport: (rows: ParsedDriverRow[]) => void;
}) {
  const [step, setStep] = useState<'upload' | 'preview'>('upload');
  const [fileName, setFileName] = useState<string>('');
  const [parsedRows, setParsedRows] = useState<ParsedDriverRow[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleDownloadTemplate = () => {
    const headers = ['name', 'phone', 'license', 'licenseExpiry', 'status', 'rating'];
    const sampleRows = [['Ahmed Khan', '+971 50 123 4567', 'DL-1234567', '2026-08-15', 'Available', '4.8'], ['Sara Al Mansoori', '+971 55 987 6543', 'DL-7654321', '2027-02-20', 'Off Duty', '4.9'], ['Mohammed Raza', '+971 52 111 2233', 'DL-8901234', '2025-11-10', 'Available', '4.6']];
    const csv = [headers.join(','), ...sampleRows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'drivers-template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };
  const handleFileSelect = (file: File) => {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target?.result as string;
      const rows = parseDriverCSV(text);
      if (rows.length < 2) {
        toast.error('CSV file is empty or missing data rows');
        return;
      }
      const headers = rows[0].map(h => h.toLowerCase());
      const nameIdx = headers.indexOf('name');
      const phoneIdx = headers.indexOf('phone');
      const licenseIdx = headers.indexOf('license');
      const expiryIdx = headers.indexOf('licenseexpiry');
      const statusIdx = headers.indexOf('status');
      const ratingIdx = headers.indexOf('rating');
      if (nameIdx === -1 || phoneIdx === -1 || licenseIdx === -1 || expiryIdx === -1) {
        toast.error('CSV must include columns: name, phone, license, licenseExpiry');
        return;
      }
      const validStatuses: Driver['status'][] = ['Available', 'On Duty', 'Off Duty'];
      const parsed: ParsedDriverRow[] = rows.slice(1).map((row, idx) => {
        const errors: string[] = [];
        const name = row[nameIdx] || '';
        const phone = row[phoneIdx] || '';
        const license = row[licenseIdx] || '';
        const licenseExpiry = row[expiryIdx] || '';
        const statusRaw = statusIdx !== -1 ? row[statusIdx] || 'Off Duty' : 'Off Duty';
        const ratingRaw = ratingIdx !== -1 ? row[ratingIdx] : '5.0';
        if (!name) errors.push('Name required');
        if (!phone) errors.push('Phone required');
        if (!license) errors.push('License required');
        if (!licenseExpiry) errors.push('License expiry required');
        // Validate date format (YYYY-MM-DD)
        if (licenseExpiry && !/^\d{4}-\d{2}-\d{2}$/.test(licenseExpiry)) {
          errors.push('Expiry must be YYYY-MM-DD');
        }
        const matchedStatus = validStatuses.find(s => s.toLowerCase() === statusRaw.toLowerCase());
        if (statusRaw && !matchedStatus) {
          errors.push(`Invalid status "${statusRaw}"`);
        }
        const rating = parseFloat(ratingRaw);
        if (ratingRaw && (isNaN(rating) || rating < 0 || rating > 5)) {
          errors.push('Rating must be 0-5');
        }
        return {
          rowNumber: idx + 2,
          name,
          phone,
          license,
          licenseExpiry,
          status: matchedStatus || 'Off Duty',
          rating: isNaN(rating) ? 5.0 : rating,
          errors
        };
      });
      setParsedRows(parsed);
      setStep('preview');
    };
    reader.readAsText(file);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };
  const validRows = parsedRows.filter(r => r.errors.length === 0);
  const invalidRows = parsedRows.filter(r => r.errors.length > 0);
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm" data-id="element-3614">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.95
    }} className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-3615">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 shrink-0" data-id="element-3616">
          <div data-id="element-3617">
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2" data-id="element-3618">
              <Upload className="w-5 h-5 text-blue-600" data-id="element-3619" />
              Bulk Upload Drivers
            </h3>
            <p className="text-sm text-gray-500" data-id="element-3620">
              {step === 'upload' ? 'Upload a CSV file to import multiple drivers at once' : `Review ${parsedRows.length} row${parsedRows.length !== 1 ? 's' : ''} before importing`}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1" data-id="element-3621">
            <X className="w-5 h-5" data-id="element-3622" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1" data-id="element-3623">
          {step === 'upload' ? <div className="space-y-6" data-id="element-3624">
              <div className="flex items-start gap-4 p-4 bg-blue-50/50 border border-blue-100 rounded-xl" data-id="element-3625">
                <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center shrink-0" data-id="element-3626">
                  <FileSpreadsheet className="w-5 h-5" data-id="element-3627" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-3628">
                  <h4 className="text-sm font-bold text-gray-900" data-id="element-3629">
                    New here? Start with our template
                  </h4>
                  <p className="text-xs text-gray-600 mt-0.5" data-id="element-3630">
                    Download the CSV template with sample rows and the exact
                    column format we expect.
                  </p>
                </div>
                <button onClick={handleDownloadTemplate} className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-blue-200 text-blue-700 rounded-lg text-xs font-bold hover:bg-blue-50 transition-colors" data-id="element-3631">
                  <Download className="w-3.5 h-3.5" data-id="element-3632" />
                  Download
                </button>
              </div>

              <div onDragOver={e => {
            e.preventDefault();
            setDragOver(true);
          }} onDragLeave={() => setDragOver(false)} onDrop={handleDrop} onClick={() => fileInputRef.current?.click()} className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${dragOver ? 'border-blue-400 bg-blue-50/50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-3633">
                <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={e => {
              const file = e.target.files?.[0];
              if (file) handleFileSelect(file);
            }} data-id="element-3634" />
                <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" data-id="element-3635" />
                <p className="text-sm font-bold text-gray-900" data-id="element-3636">
                  Click to upload or drag and drop
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-3637">CSV file, max 5MB</p>
              </div>

              <div className="text-xs text-gray-500" data-id="element-3638">
                <p className="font-semibold text-gray-700 mb-1" data-id="element-3639">
                  Required columns:
                </p>
                <div className="flex flex-wrap gap-1.5" data-id="element-3640">
                  {['name', 'phone', 'license', 'licenseExpiry'].map(c => <span key={c} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded font-mono" data-id="element-3641">
                      {c}
                    </span>)}
                </div>
                <p className="font-semibold text-gray-700 mt-2 mb-1" data-id="element-3642">
                  Optional columns:
                </p>
                <div className="flex flex-wrap gap-1.5" data-id="element-3643">
                  {['status', 'rating'].map(c => <span key={c} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded font-mono" data-id="element-3644">
                      {c}
                    </span>)}
                </div>
              </div>
            </div> : <div className="space-y-4" data-id="element-3645">
              <div className="grid grid-cols-2 gap-3" data-id="element-3646">
                <div className="p-3 bg-green-50 border border-green-100 rounded-xl" data-id="element-3647">
                  <div className="flex items-center gap-2 mb-1" data-id="element-3648">
                    <CheckCircle2 className="w-4 h-4 text-green-600" data-id="element-3649" />
                    <p className="text-xs font-bold text-green-700 uppercase tracking-wider" data-id="element-3650">
                      Ready to import
                    </p>
                  </div>
                  <p className="text-2xl font-display font-bold text-gray-900" data-id="element-3651">
                    {validRows.length}
                  </p>
                </div>
                <div className={`p-3 border rounded-xl ${invalidRows.length > 0 ? 'bg-red-50 border-red-100' : 'bg-gray-50 border-gray-100'}`} data-id="element-3652">
                  <div className="flex items-center gap-2 mb-1" data-id="element-3653">
                    <AlertCircle className={`w-4 h-4 ${invalidRows.length > 0 ? 'text-red-600' : 'text-gray-400'}`} data-id="element-3654" />
                    <p className={`text-xs font-bold uppercase tracking-wider ${invalidRows.length > 0 ? 'text-red-700' : 'text-gray-500'}`} data-id="element-3655">
                      Has errors
                    </p>
                  </div>
                  <p className="text-2xl font-display font-bold text-gray-900" data-id="element-3656">
                    {invalidRows.length}
                  </p>
                </div>
              </div>

              <div className="text-xs text-gray-500" data-id="element-3657">
                File:{' '}
                <span className="font-medium text-gray-700" data-id="element-3658">{fileName}</span>
              </div>

              <div className="border border-gray-200 rounded-xl overflow-hidden" data-id="element-3659">
                <div className="max-h-80 overflow-y-auto" data-id="element-3660">
                  <table className="w-full text-xs" data-id="element-3661">
                    <thead className="bg-gray-50 text-gray-500 uppercase tracking-wider sticky top-0" data-id="element-3662">
                      <tr data-id="element-3663">
                        <th className="px-3 py-2 text-left font-bold" data-id="element-3664">#</th>
                        <th className="px-3 py-2 text-left font-bold" data-id="element-3665">Name</th>
                        <th className="px-3 py-2 text-left font-bold" data-id="element-3666">Phone</th>
                        <th className="px-3 py-2 text-left font-bold" data-id="element-3667">
                          License
                        </th>
                        <th className="px-3 py-2 text-left font-bold" data-id="element-3668">
                          Expiry
                        </th>
                        <th className="px-3 py-2 text-left font-bold" data-id="element-3669">
                          Status
                        </th>
                        <th className="px-3 py-2 text-left font-bold" data-id="element-3670">
                          Issues
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100" data-id="element-3671">
                      {parsedRows.map(row => <tr key={row.rowNumber} className={row.errors.length > 0 ? 'bg-red-50/50' : ''} data-id="element-3672">
                          <td className="px-3 py-2 text-gray-500 font-mono" data-id="element-3673">
                            {row.rowNumber}
                          </td>
                          <td className="px-3 py-2 font-medium text-gray-900" data-id="element-3674">
                            {row.name || <span className="text-red-500 italic" data-id="element-3675">empty</span>}
                          </td>
                          <td className="px-3 py-2 text-gray-700" data-id="element-3676">
                            {row.phone || <span className="text-red-500 italic" data-id="element-3677">empty</span>}
                          </td>
                          <td className="px-3 py-2 text-gray-700" data-id="element-3678">
                            {row.license || <span className="text-red-500 italic" data-id="element-3679">empty</span>}
                          </td>
                          <td className="px-3 py-2 text-gray-700" data-id="element-3680">
                            {row.licenseExpiry || <span className="text-red-500 italic" data-id="element-3681">empty</span>}
                          </td>
                          <td className="px-3 py-2 text-gray-700" data-id="element-3682">
                            {row.status}
                          </td>
                          <td className="px-3 py-2" data-id="element-3683">
                            {row.errors.length === 0 ? <span className="inline-flex items-center gap-1 text-green-600" data-id="element-3684">
                                <CheckCircle2 className="w-3 h-3" data-id="element-3685" /> OK
                              </span> : <span className="text-red-600" data-id="element-3686">
                                {row.errors.join(', ')}
                              </span>}
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>}
        </div>

        <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-between items-center shrink-0" data-id="element-3687">
          {step === 'preview' ? <button onClick={() => {
          setStep('upload');
          setParsedRows([]);
          setFileName('');
        }} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 bg-gray-100 rounded-xl transition-colors" data-id="element-3688">
              Back
            </button> : <div data-id="element-3689"></div>}
          <div className="flex gap-3" data-id="element-3690">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl" data-id="element-3691">
              Cancel
            </button>
            {step === 'preview' && <button onClick={() => onImport(validRows)} disabled={validRows.length === 0} className="px-4 py-2 text-sm font-bold text-white bg-gray-900 hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-xl" data-id="element-3692">
                Import {validRows.length} driver
                {validRows.length !== 1 ? 's' : ''}
              </button>}
          </div>
        </div>
      </motion.div>
    </div>;
}